/**
 * Hooks LeadBeton Sprint 3 (Modules 7 Stocks audit · 8 Expéditions BL).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type CreateExpeditionPayload,
  type CreateMouvementStockPayload,
  type ExpeditionListParams,
  type MouvementStockListParams,
  type RefuserExpeditionPayload,
  type SignerLivraisonPayload,
  type UpdateExpeditionPayload,
  type UpdateMatiereTypagePayload,
} from '@/api/operations';

const STOCKS_MVT_KEY    = ['operations', 'beton', 'stocks', 'mouvements']  as const;
const STOCKS_HEALTH_KEY = ['operations', 'beton', 'stocks', 'health']      as const;
const EXPEDITIONS_KEY   = ['operations', 'beton', 'expeditions']           as const;
const MATIERES_KEY      = ['operations', 'beton', 'matieres']              as const;

// ── Stocks audit ─────────────────────────────────────────────────────────

export const useMouvementsStock = (params: MouvementStockListParams = {}) =>
  useQuery({
    queryKey:  [...STOCKS_MVT_KEY, params],
    queryFn:   () => operationsApi.beton.listMouvementsStock(params),
    staleTime: 30_000,
  });

export const useCreateMouvementStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMouvementStockPayload) => operationsApi.beton.createMouvementStock(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STOCKS_MVT_KEY });
      qc.invalidateQueries({ queryKey: STOCKS_HEALTH_KEY });
      qc.invalidateQueries({ queryKey: MATIERES_KEY });
    },
  });
};

export const useUpdateMatiereTypage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMatiereTypagePayload }) =>
      operationsApi.beton.updateMatiereTypage(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: MATIERES_KEY }),
  });
};

export const useStockHealth = () =>
  useQuery({
    queryKey:        STOCKS_HEALTH_KEY,
    queryFn:         () => operationsApi.beton.getStockHealth(),
    staleTime:       60_000,
    refetchInterval: 120_000,
  });

// ── Expéditions ──────────────────────────────────────────────────────────

export const useExpeditions = (params: ExpeditionListParams = {}) =>
  useQuery({
    queryKey:  [...EXPEDITIONS_KEY, params],
    queryFn:   () => operationsApi.beton.listExpeditions(params),
    staleTime: 30_000,
  });

export const useExpedition = (id: string | undefined) =>
  useQuery({
    queryKey:  [...EXPEDITIONS_KEY, id],
    queryFn:   () => operationsApi.beton.getExpedition(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

const invalidateExpeditions = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: EXPEDITIONS_KEY });
};

export const useCreateExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateExpeditionPayload) => operationsApi.beton.createExpedition(payload),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useUpdateExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateExpeditionPayload }) =>
      operationsApi.beton.updateExpedition(id, payload),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const usePreparerExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.preparerExpedition(id),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useExpedierExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.expedierExpedition(id),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useLivrerExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SignerLivraisonPayload }) =>
      operationsApi.beton.livrerExpedition(id, payload),
    onSuccess: () => {
      invalidateExpeditions(qc);
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'parc'] });
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'lots'] });
    },
  });
};

// BL-TERRAIN — visa du DAF, et signatures (adresses temporaires : relues à chaque ouverture, jamais gardées en cache).
export const useViserExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => operationsApi.beton.viserExpedition(id, note),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useRevoquerLienClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.revoquerLienClient(id),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useSignaturesExpedition = (id: string | undefined) =>
  useQuery({
    queryKey: [...EXPEDITIONS_KEY, id, 'signatures'],
    queryFn: () => operationsApi.beton.signaturesExpedition(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });

export const useRefuserExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RefuserExpeditionPayload }) =>
      operationsApi.beton.refuserExpedition(id, payload),
    onSuccess: () => invalidateExpeditions(qc),
  });
};

export const useDeleteExpedition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteExpedition(id),
    onSuccess: () => invalidateExpeditions(qc),
  });
};
